/**
 * Import/Export Jobs Panel
 * Unified list of all import and export jobs with status and actions
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Download,
  Upload,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  RefreshCw,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  FileJson,
  FileSpreadsheet,
  Trash2,
  AlertTriangle,
  School,
  FileQuestion,
  MoreVertical,
  RotateCcw,
  Ban
} from 'lucide-react'
import api from '../../services/api'
import webSocketService from '../../services/webSocketService'
import { useDebounce } from '../../hooks/useDebounce'
import toast from 'react-hot-toast'
import { createLogger } from '../../utils/logger'

const logger = createLogger('ImportExportJobsPanel')

const ITEMS_PER_PAGE = 10

const ImportExportJobsPanel = ({
  isOpen,
  onClose,
  isInline = false,
  onOpenImport,
  onOpenSchoolImport,
  onNavigateToQuestions,
  // Bumped by the parent when an import modal reports a finished job, so the list
  // refreshes immediately instead of waiting for the next poll tick.
  externalRefreshSignal = 0
}) => {
  const [jobs, setJobs] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    pages: 1
  })

  // Use ref to track active jobs without causing re-renders
  const jobsRef = useRef([])
  // Track the currently displayed page so background refreshes (polling /
  // websocket) re-fetch the same page instead of snapping back to page 1.
  const pageRef = useRef(1)
  // Monotonic load id so an out-of-order/superseded response (polling and a
  // debounced websocket refetch can overlap) never clobbers newer data.
  const loadSeqRef = useRef(0)

  // Bumped on each server-side job lifecycle event; debounced so a rapid
  // PENDING -> PROCESSING -> COMPLETED burst coalesces into one trailing
  // refetch (the last event wins, so the final state is never dropped).
  const [refreshSignal, setRefreshSignal] = useState(0)
  const debouncedRefreshSignal = useDebounce(refreshSignal, 400)

  // Fold a parent-driven refresh request into the same debounced refetch path
  // used by websocket events. Skipped on mount (0) since the panel loads anyway.
  useEffect(() => {
    if (externalRefreshSignal > 0) setRefreshSignal((s) => s + 1)
  }, [externalRefreshSignal])

  // Delete confirmation dialog state
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    type: null, // 'export' or 'import'
    importType: null, // 'QUESTIONS' or 'SCHOOLS' (for imports only)
    id: null,
    name: ''
  })

  // Expanded error details
  const [expandedError, setExpandedError] = useState(null)

  // Dropdown menu state
  const [openDropdown, setOpenDropdown] = useState(null)
  const dropdownRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const loadJobs = useCallback(async (showLoading = true, page = 1) => {
    // Record the page being loaded up front so concurrent background refreshes
    // (polling / websocket) target this page, not a stale one mid-pagination.
    pageRef.current = page
    const seq = ++loadSeqRef.current
    if (showLoading) setIsLoading(true)
    else setIsRefreshing(true)

    try {
      const [exportsRes, questionImportsRes, schoolImportsRes] = await Promise.all([
        api.get(`/admin/exports?limit=${ITEMS_PER_PAGE}&page=${page}`),
        api.get(`/admin/imports?limit=${ITEMS_PER_PAGE}&page=${page}`),
        api.get(`/admin/schools/imports?limit=${ITEMS_PER_PAGE}&page=${page}`)
      ])

      // A newer load started while this one was in flight; drop this result so
      // it can't overwrite fresher data (or the wrong page) on screen.
      if (seq !== loadSeqRef.current) return

      // Transform exports to unified format
      const exports = (exportsRes.data?.exports || []).map((exp) => ({
        ...exp,
        jobType: 'EXPORT',
        contentType: 'QUESTIONS'
      }))

      // Transform question imports
      const questionImports = (questionImportsRes.data?.imports || []).map((imp) => ({
        ...imp,
        jobType: 'IMPORT',
        contentType: 'QUESTIONS'
      }))

      // Transform school imports
      const schoolImports = (schoolImportsRes.data?.imports || []).map((imp) => ({
        ...imp,
        jobType: 'IMPORT',
        contentType: 'SCHOOLS'
      }))

      // Combine and sort all jobs by creation date (most recent first)
      const allJobs = [...exports, ...questionImports, ...schoolImports].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      )

      setJobs(allJobs)

      // Calculate combined pagination
      const exportTotal = exportsRes.data?.pagination?.total || 0
      const questionTotal = questionImportsRes.data?.pagination?.total || 0
      const schoolTotal = schoolImportsRes.data?.pagination?.total || 0
      const total = exportTotal + questionTotal + schoolTotal

      setPagination({
        page,
        total,
        pages: Math.ceil(total / ITEMS_PER_PAGE)
      })

      // Update ref for background-refresh active-job check
      jobsRef.current = allJobs
    } catch (error) {
      // Only surface the error for the latest load; a superseded one is moot.
      if (seq === loadSeqRef.current) {
        logger.error('Failed to load jobs:', error)
        toast.error('Failed to load jobs')
      }
    } finally {
      // Only the latest load controls the loading indicators, so a stale
      // response finishing first doesn't clear them while a newer load runs.
      if (seq === loadSeqRef.current) {
        setIsLoading(false)
        setIsRefreshing(false)
      }
    }
  }, [])

  const loadPage = useCallback(
    async (page) => {
      setIsRefreshing(true)
      await loadJobs(false, page)
    },
    [loadJobs]
  )

  // Load jobs when the panel opens or when inline, then keep the list fresh
  // via websocket job-update events with adaptive polling as a safety net.
  useEffect(() => {
    if (!isInline && !isOpen) return undefined

    loadJobs()

    // Adaptive polling: refresh quickly while jobs are active, slowly when
    // idle. Polling always runs (it is NOT gated on the current snapshot) so
    // jobs created elsewhere -- or that complete between ticks -- still show up
    // even if a websocket event is missed.
    const BASE_TICK_MS = 4000
    const IDLE_EVERY_N_TICKS = 3 // refresh ~every 12s when nothing is active
    let tick = 0
    const interval = setInterval(() => {
      tick += 1
      const hasActiveJobs = jobsRef.current.some((job) => {
        const status = job.status?.toLowerCase()
        return ['pending', 'processing', 'validating'].includes(status)
      })
      if (hasActiveJobs || tick % IDLE_EVERY_N_TICKS === 0) {
        loadJobs(false, pageRef.current)
      }
    }, BASE_TICK_MS)

    // Bump the refresh signal on any server-side job lifecycle event; the
    // debounced effect below turns a burst into a single trailing refetch.
    const handleJobUpdate = () => setRefreshSignal((s) => s + 1)
    webSocketService.on('admin:job:update', handleJobUpdate)

    return () => {
      clearInterval(interval)
      webSocketService.off('admin:job:update', handleJobUpdate)
    }
  }, [isInline, isOpen, loadJobs])

  // Trailing-debounced refetch driven by websocket job-update events. Guarded
  // on visibility so a signal that lands just before the panel hides doesn't
  // trigger a stray background fetch.
  useEffect(() => {
    if (!isInline && !isOpen) return
    if (debouncedRefreshSignal > 0) {
      loadJobs(false, pageRef.current)
    }
  }, [debouncedRefreshSignal, isInline, isOpen, loadJobs])

  const handleDownload = (exportId) => {
    window.open(`/api/admin/questions/export/${exportId}/download`, '_blank')
  }

  const handleRetry = async (job) => {
    try {
      const endpoint =
        job.contentType === 'SCHOOLS'
          ? `/admin/schools/import/${job.id}/retry`
          : `/admin/questions/import/${job.id}/retry`
      await api.post(endpoint)
      toast.success('Retry started')
      setOpenDropdown(null)
      loadJobs(false)
    } catch (error) {
      toast.error(error.message || 'Failed to retry import')
    }
  }

  const handleCancel = async (job) => {
    try {
      const endpoint =
        job.contentType === 'SCHOOLS'
          ? `/admin/schools/import/${job.id}/cancel`
          : `/admin/questions/import/${job.id}/cancel`
      await api.post(endpoint)
      toast.success('Import cancelled')
      setOpenDropdown(null)
      loadJobs(false)
    } catch (error) {
      toast.error(error.message || 'Failed to cancel import')
    }
  }

  const openDeleteDialog = (job) => {
    const name =
      job.jobType === 'EXPORT'
        ? `${job.totalRecords || '?'} questions (${job.format})`
        : job.originalName || 'Unknown file'
    setDeleteDialog({
      isOpen: true,
      type: job.jobType === 'EXPORT' ? 'export' : 'import',
      id: job.id,
      name,
      importType: job.contentType
    })
    setOpenDropdown(null)
  }

  const closeDeleteDialog = () => {
    setDeleteDialog({ isOpen: false, type: null, id: null, name: '', importType: null })
  }

  const handleDelete = async () => {
    const { type, id, importType } = deleteDialog
    try {
      if (type === 'export') {
        await api.delete(`/admin/questions/export/${id}`)
        toast.success('Export deleted')
      } else if (type === 'import') {
        const endpoint =
          importType === 'SCHOOLS' ? `/admin/schools/import/${id}` : `/admin/questions/import/${id}`
        await api.delete(endpoint)
        toast.success('Import deleted')
      }
      closeDeleteDialog()
      loadJobs(false)
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Failed to delete job')
    }
  }

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase()
    switch (statusLower) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/50 rounded-full">
            <CheckCircle className="w-3 h-3" />
            Completed
          </span>
        )
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-red-700 dark:text-red-300 bg-red-100 dark:bg-red-900/50 rounded-full">
            <XCircle className="w-3 h-3" />
            Failed
          </span>
        )
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-full">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        )
      case 'processing':
      case 'validating':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/50 rounded-full">
            <Loader2 className="w-3 h-3 animate-spin" />
            {statusLower === 'validating' ? 'Validating' : 'Processing'}
          </span>
        )
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-full">
            <X className="w-3 h-3" />
            Cancelled
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-full">
            {status}
          </span>
        )
    }
  }

  const getJobTypeBadge = (jobType) => {
    if (jobType === 'EXPORT') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/50 rounded">
          <Download className="w-3 h-3" />
          Export
        </span>
      )
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-900/50 rounded">
        <Upload className="w-3 h-3" />
        Import
      </span>
    )
  }

  const getContentTypeBadge = (contentType) => {
    if (contentType === 'SCHOOLS') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/50 rounded">
          <School className="w-3 h-3" />
          Schools
        </span>
      )
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/50 rounded">
        <FileQuestion className="w-3 h-3" />
        Questions
      </span>
    )
  }

  const getJobIcon = (job) => {
    if (job.jobType === 'EXPORT') {
      return job.format === 'json' ? (
        <FileJson className="w-8 h-8 text-primary-500" />
      ) : (
        <FileSpreadsheet className="w-8 h-8 text-green-500" />
      )
    }
    return job.contentType === 'SCHOOLS' ? (
      <School className="w-8 h-8 text-blue-500" />
    ) : (
      <FileQuestion className="w-8 h-8 text-purple-500" />
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatSize = (bytes) => {
    if (!bytes) return '-'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // Action dropdown menu
  const renderActionsDropdown = (job) => {
    const isOpen = openDropdown === job.id
    const status = job.status?.toLowerCase()
    const isExport = job.jobType === 'EXPORT'
    const isFailed = status === 'failed'
    const isPending = status === 'pending'
    const isCompleted = status === 'completed'

    const hasActions =
      (isExport && isCompleted && job.downloadUrl) || (!isExport && (isFailed || isPending)) || true // Delete always available

    if (!hasActions) return null

    return (
      <div className="relative" ref={isOpen ? dropdownRef : null}>
        <button
          onClick={(e) => {
            e.stopPropagation()
            setOpenDropdown(isOpen ? null : job.id)
          }}
          className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-600"
          title="Actions"
        >
          <MoreVertical className="w-4 h-4" />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-1 w-44 bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 py-1 z-10">
            {/* Download (exports only) */}
            {isExport && isCompleted && job.downloadUrl && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleDownload(job.id)
                  setOpenDropdown(null)
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
              >
                <Download className="w-4 h-4 text-primary-500" />
                Download
              </button>
            )}

            {/* Retry (failed imports only) */}
            {!isExport && isFailed && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleRetry(job)
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
              >
                <RotateCcw className="w-4 h-4 text-yellow-500" />
                Retry
              </button>
            )}

            {/* Cancel (pending imports only) */}
            {!isExport && isPending && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleCancel(job)
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600"
              >
                <Ban className="w-4 h-4 text-gray-500" />
                Cancel
              </button>
            )}

            {/* Divider before delete */}
            <div className="border-t border-gray-200 dark:border-gray-600 my-1" />

            {/* Delete */}
            <button
              onClick={(e) => {
                e.stopPropagation()
                openDeleteDialog(job)
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        )}
      </div>
    )
  }

  // Pagination component
  const renderPagination = () => {
    if (pagination.pages <= 1) return null

    const startItem = (pagination.page - 1) * ITEMS_PER_PAGE + 1
    const endItem = Math.min(pagination.page * ITEMS_PER_PAGE, pagination.total)

    return (
      <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-600 rounded-b-lg">
        {/* Mobile pagination */}
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => loadPage(pagination.page - 1)}
            disabled={pagination.page <= 1 || isRefreshing}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => loadPage(pagination.page + 1)}
            disabled={pagination.page >= pagination.pages || isRefreshing}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>

        {/* Desktop pagination */}
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Showing <span className="font-medium">{startItem}</span> to{' '}
              <span className="font-medium">{endItem}</span> of{' '}
              <span className="font-medium">{pagination.total}</span> jobs
            </p>
          </div>
          <div>
            <nav
              className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
              aria-label="Pagination"
            >
              <button
                onClick={() => loadPage(pagination.page - 1)}
                disabled={pagination.page <= 1 || isRefreshing}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="ml-1">Previous</span>
              </button>
              <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => loadPage(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages || isRefreshing}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="mr-1">Next</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    )
  }

  const renderJobRow = (job) => {
    const status = job.status?.toLowerCase()
    const isExpanded = expandedError === job.id
    const isFailed = status === 'failed'
    const isProcessing = ['validating', 'processing'].includes(status)
    const isExport = job.jobType === 'EXPORT'
    const isCompleted = status === 'completed'

    return (
      <div key={job.id} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {getJobIcon(job)}
            <div className="min-w-0 flex-1">
              {/* Title and badges row */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-gray-900 dark:text-white truncate">
                  {isExport
                    ? `${job.totalRecords?.toLocaleString() || '?'} questions`
                    : job.originalName || 'Unknown file'}
                </span>
                {getJobTypeBadge(job.jobType)}
                {getContentTypeBadge(job.contentType)}
                {getStatusBadge(job.status)}
              </div>
              {/* Metadata row */}
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {formatDate(job.createdAt)}
                {job.fileSize && ` • ${formatSize(job.fileSize)}`}
                {job.metadata?.mode && ` • ${job.metadata.mode}`}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Show error details toggle for failed jobs */}
            {isFailed && (
              <button
                onClick={() => setExpandedError(isExpanded ? null : job.id)}
                className="flex items-center gap-1 px-2 py-1 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
              >
                <AlertTriangle className="w-4 h-4" />
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>
            )}

            {/* Quick download button for completed exports */}
            {isExport && isCompleted && job.downloadUrl && (
              <button
                onClick={() => handleDownload(job.id)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded hover:bg-primary-700"
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            )}

            {/* Actions dropdown */}
            {renderActionsDropdown(job)}
          </div>
        </div>

        {/* Progress bar for active jobs */}
        {isProcessing && !isExport && job.totalRecords > 0 && (
          <div className="mt-2">
            <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 transition-all duration-300"
                style={{
                  width: `${Math.round((job.processedRecords / job.totalRecords) * 100)}%`
                }}
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {job.processedRecords?.toLocaleString() || 0} /{' '}
              {job.totalRecords?.toLocaleString() || 0}
            </p>
          </div>
        )}

        {/* Results for completed imports */}
        {!isExport && isCompleted && (
          <div className="mt-2 flex gap-4 text-sm">
            <span className="text-green-600 dark:text-green-400">
              {job.successCount?.toLocaleString() || 0} imported
            </span>
            {job.errorCount > 0 && (
              <span className="text-yellow-600 dark:text-yellow-400">
                {job.errorCount?.toLocaleString() || 0} skipped
              </span>
            )}
          </div>
        )}

        {/* Expandable error details */}
        {isFailed && isExpanded && (
          <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-800">
            <div className="flex items-start gap-2">
              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-red-800 dark:text-red-300">
                  {isExport ? 'Export Failed' : `Import Failed - ${job.errorCount || 0} error(s)`}
                </p>
                {isExport ? (
                  <p className="mt-1 text-sm text-red-700 dark:text-red-400 break-words">
                    {job.errorMessage || 'An unknown error occurred'}
                  </p>
                ) : job.errors && job.errors.length > 0 ? (
                  <ul className="mt-2 space-y-2 text-sm text-red-700 dark:text-red-400">
                    {job.errors.slice(0, 5).map((err, idx) => (
                      <li key={idx} className="break-words">
                        {err.row && <span className="font-medium">Row {err.row}: </span>}
                        {err.message || err.errors?.join(', ') || JSON.stringify(err)}
                      </li>
                    ))}
                    {job.errors.length > 5 && (
                      <li className="text-red-500 dark:text-red-400 italic">
                        ... and {job.errors.length - 5} more errors
                      </li>
                    )}
                  </ul>
                ) : (
                  <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                    An unknown error occurred during import
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderContent = () => (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <div className="flex justify-center gap-2 mb-3">
            <Download className="w-10 h-10 text-gray-300 dark:text-gray-600" />
            <Upload className="w-10 h-10 text-gray-300 dark:text-gray-600" />
          </div>
          <p className="text-lg font-medium">No jobs yet</p>
          <p className="text-sm mt-2">Exports are created from the Questions tab</p>
          <div className="mt-4 flex gap-2 justify-center flex-wrap">
            {onNavigateToQuestions && (
              <button
                onClick={onNavigateToQuestions}
                className="px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 border border-primary-300 dark:border-primary-600 rounded"
              >
                Go to Questions
              </button>
            )}
            {onOpenImport && (
              <button
                onClick={onOpenImport}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded hover:bg-primary-700"
              >
                Import Questions
              </button>
            )}
            {onOpenSchoolImport && (
              <button
                onClick={onOpenSchoolImport}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded hover:bg-gray-50 dark:hover:bg-gray-500"
              >
                Import Schools
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="space-y-3">{jobs.map(renderJobRow)}</div>
          {renderPagination()}
        </>
      )}
    </>
  )

  // Delete Confirmation Dialog
  const renderDeleteDialog = () => {
    if (!deleteDialog.isOpen) return null

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50" onClick={closeDeleteDialog} />

        {/* Dialog */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-red-100 dark:bg-red-900/50">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Delete {deleteDialog.type === 'export' ? 'Export' : 'Import'}
              </h3>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Are you sure you want to delete this {deleteDialog.type}?
                {deleteDialog.name && (
                  <span className="block mt-1 font-medium text-gray-900 dark:text-white">
                    {deleteDialog.name}
                  </span>
                )}
              </p>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                This will permanently remove the job record and any associated files. This action
                cannot be undone.
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={closeDeleteDialog}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Stats summary bar
  const renderStatsSummary = () => {
    const exportCount = jobs.filter((j) => j.jobType === 'EXPORT').length
    const importCount = jobs.filter((j) => j.jobType === 'IMPORT').length
    const activeCount = jobs.filter((j) => {
      const status = j.status?.toLowerCase()
      return ['pending', 'processing', 'validating'].includes(status)
    }).length

    return (
      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4 flex-wrap">
        <span className="inline-flex items-center gap-1.5">
          <span className="font-medium text-gray-900 dark:text-white">{pagination.total}</span>
          total jobs
        </span>
        <span className="text-gray-300 dark:text-gray-600">|</span>
        <span className="inline-flex items-center gap-1.5">
          <Download className="w-4 h-4 text-emerald-500" />
          {exportCount} exports
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Upload className="w-4 h-4 text-indigo-500" />
          {importCount} imports
        </span>
        {activeCount > 0 && (
          <>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              {activeCount} active
            </span>
          </>
        )}
      </div>
    )
  }

  // Inline mode - render in page flow
  if (isInline) {
    return (
      <>
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Import/Export Jobs
            </h3>
            <button
              onClick={() => loadJobs(false)}
              disabled={isRefreshing}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="card-body">
            {!isLoading && jobs.length > 0 && renderStatsSummary()}
            {renderContent()}
          </div>
        </div>
        {renderDeleteDialog()}
      </>
    )
  }

  // Modal mode (existing behavior)
  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/50" onClick={onClose} />

        {/* Panel */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Import/Export Jobs
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => loadJobs(false)}
                disabled={isRefreshing}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {!isLoading && jobs.length > 0 && renderStatsSummary()}
            {renderContent()}
          </div>
        </div>
      </div>
      {renderDeleteDialog()}
    </>
  )
}

export default ImportExportJobsPanel
